import { yupResolver } from '@hookform/resolvers/yup';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Dialog } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { setPersonalInformation } from 'v3/data/store/person/actions';
import { IPersonalInformation } from 'v3/data/store/person/types';
import { IContractingDetailsParams } from 'v3/pages/Contracting/Details/Cards/types';
import * as api from 'v3/services/api';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { TextButton } from 'v3/shared/components/Button/TextButton';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import { TextMaskPhone } from 'v3/shared/components/Inputs/MaskPhoneInput';

import {
  AlignButtons,
  CloseIconStyled,
  EmailAndPhone,
  Gap,
  MainContainer,
  SecondaryContainer,
  TitleContainer,
  paperStyles,
} from 'v3/shared/components/Modal/ChangePersonalInformationsModal/styles';
import { IChangePersonalInformationsModalProps } from 'v3/shared/components/Modal/ChangePersonalInformationsModal/types';
import { changePersonalInformationsSchema } from 'v3/shared/schemas/changePersonalInformations';
import { SubtitleLarge } from 'v3/shared/typography/SubtitleLarge';
import { theme } from 'v3/styles/global-styles';

export const ChangePersonalInformationsModal: React.FC<IChangePersonalInformationsModalProps> = ({
  active,
  onClose,
}) => {
  const { contractingKey } = useParams<Readonly<IContractingDetailsParams>>() as IContractingDetailsParams;
  const { person } = useStoreSelector((store) => store.person);

  const {
    control,
    getValues,
    reset,
    setError,
    formState: { isValid, errors },
  } = useForm<IPersonalInformation>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    values: {
      ...person,
      phoneNumber: `(${person.phoneAreaCode})${person.phoneNumber}`,
    },
    resolver: yupResolver(changePersonalInformationsSchema) as any,
  });

  useEffect(() => {
    reset({
      ...person,
      phoneNumber: `(${person.phoneAreaCode})${person.phoneNumber}`,
    });
  }, [active, person, reset]);

  const formatPhoneNumber = (phone: string) => {
    const onlyNumbers = phone.replace(/[^0-9]/g, '');
    const areaCode = onlyNumbers.substring(0, 2).replace(/^0+/, '');
    const number = onlyNumbers.substring(2);
    return { areaCode, number };
  };

  const handleUpdatePersonalInformations = async () => {
    const { areaCode, number } = formatPhoneNumber(getValues('phoneNumber'));

    const updatedInfo = {
      fullName: getValues('fullName').trim(),
      email: getValues('email').trim(),
      phoneNumber: number,
      phoneAreaCode: areaCode,
    };
    const isEmailAvailable = await verifyEmailAvailability(updatedInfo.email, false);

    if (!isEmailAvailable) {
      return;
    }

    try {
      await api.jazzUpdatePerson(contractingKey, updatedInfo);
      setPersonalInformation({
        ...person,
        fullName: updatedInfo.fullName,
        email: updatedInfo.email,
        phoneNumber: updatedInfo.phoneNumber,
        phoneAreaCode: updatedInfo.phoneAreaCode,
      });
      onClose();
    } catch (error) {
      showErrorDialog('Erro ao atualizar as informações pessoais', error);
    }
  };

  const verifyEmailAvailability = async (email: string, hideLoading?: boolean) => {
    const isValidEmail = !errors.email;
    if (email && isValidEmail) {
      const response = await api.verifyEmailAvailability(email, hideLoading);
      if (!response.data.available) {
        setError('email', { message: 'Este e-mail já está em uso, tente outro e-mail.' });
        return false;
      }
      return true;
    }
  };

  return (
    <Dialog
      onClose={onClose}
      open={active}
      aria-labelledby="customized-dialog-title"
      aria-describedby="customized-dialog-description"
      PaperProps={paperStyles}
    >
      <MainContainer>
        <TitleContainer>
          <SecondaryContainer>
            <ListAltIcon style={{ color: theme.palette.error.main }} />
            <SubtitleLarge>Editar informações do cliente</SubtitleLarge>
          </SecondaryContainer>
          <CloseIconStyled onClick={onClose} />
        </TitleContainer>
        <Gap>
          <InputFormController
            data-testid="input-full-name"
            formControl={control}
            formControlName="fullName"
            label="Nome completo"
          />
          <EmailAndPhone>
            <InputFormController
              data-testid="input-email"
              formControl={control}
              formControlName="email"
              onBlurInput={(value) => verifyEmailAvailability(value)}
              label="E-mail"
            />
            <InputFormController
              data-testid="input-phone-number"
              formControl={control}
              formControlName="phoneNumber"
              label="Telefone"
              mask="(00)00000-0000"
              placeholder="(__)_____-____"
              customMask={TextMaskPhone}
              inputType="tel"
            />
          </EmailAndPhone>
          <AlignButtons>
            <TextButton text="Cancelar" $outline size="small" handleClick={onClose} />
            <FlexButton
              text="Salvar alterações"
              size="small"
              disabled={!isValid || !!errors.email}
              handleClick={handleUpdatePersonalInformations}
            />
          </AlignButtons>
        </Gap>
      </MainContainer>
    </Dialog>
  );
};
